import { Entity } from "@app/entity/model/entity";
import * as EntitySelectors from "@app/entity/store/selectors/entity.selectors";
import { CallState, LoadingState } from "@app/root-store/api-result.state";
import { RouterSelectors } from "@app/root-store/selectors";
import { ClareDbCollection } from "@app/shared/resources/resource-objectifier";
import { User } from "@app/user/model/user";
import { UserRole } from "@app/user/model/user-role";
import { MemoizedSelector, createFeatureSelector, createSelector } from "@ngrx/store";
import { UserManagementState, initialUserManagementState } from "../user-management.state";

export const selectState = createFeatureSelector<UserManagementState>("user-management");

// This will return Users/Staffs
export const getAllUsers: MemoizedSelector<UserManagementState, User[]> = createSelector(selectState, (state: UserManagementState) =>
    state?.users ? Object.values(state.users.result || {}).sort((a, b) => (a.firstName || "").localeCompare(b.firstName || "")) : []
);

// This will return Customers
export const getAllCustomers: MemoizedSelector<UserManagementState, User[]> = createSelector(selectState, (state: UserManagementState) =>
    state?.customers
        ? Object.values(state.customers?.result || {}).sort((a, b) => (a.firstName || "").localeCompare(b.firstName || ""))
        : []
);

export const getCustomerRoleId: MemoizedSelector<UserManagementState, number> = createSelector(
    selectState,
    (state: UserManagementState) => state?.customerRoleId || null
);

export const getUsers: MemoizedSelector<UserManagementState, User[]> = createSelector(
    selectState,
    getAllUsers,
    (state: UserManagementState, users: User[]) => (users ? users.filter(user => user.roleId !== state.customerRoleId) : [])
);

export const getEntityStaffState: MemoizedSelector<UserManagementState, { result: User[]; entityId: number; callState: CallState<any> }> =
    createSelector(selectState, (state: UserManagementState) => state?.activeEntityStaff);

export const getEntityStaffs: MemoizedSelector<UserManagementState, User[]> = createSelector(
    getEntityStaffState,
    activeEntityStaff => activeEntityStaff.result
);
/**
 *
 * @param staffId
 * @returns
 */
export const getSelectedStaff = (staffId: number) =>
    createSelector(getEntityStaffs, staffs => (staffs || []).find(staff => staff.id === staffId));

export const getUsersObject = createSelector(selectState, (state: UserManagementState) => state?.users?.result || {});

export const getActiveProjectUserState = createSelector(
    selectState,
    (state: UserManagementState = initialUserManagementState) => state.activeProjectUser || { result: null, callState: LoadingState.init }
);

export const getAllUsersByEntityId = (entityId: number) =>
    createSelector(getAllUsers, (users: User[]) => users?.filter(user => user.entityId === entityId) || []);

export const getUser = (id: number) => createSelector(getUsersObject, (users: ClareDbCollection<User>) => users?.[id] || undefined);

export const getCurrentUser = createSelector(
    RouterSelectors.selectUrlParam("userId"),
    getUsersObject,
    (id, users: ClareDbCollection<User>) => getUser(+id).projector(users)
);

/**
 * the user should refer to the staff, not customer
 *
 * @param email
 * @returns
 */
export const checkUserExistenceByEmail = (email?: string) =>
    createSelector(
        getAllUsers,
        getCustomerRoleId,
        (users: User[] = [], customerRoleId = null) =>
            !!users.filter(user => (user.email || "").toLowerCase() === (email || "").toLowerCase() && user.roleId !== customerRoleId)
                .length
    );

export const getUserByEmailExactMatch = (email?: string) =>
    createSelector(getAllUsers, (users: User[] = []) => users.filter(user => user.email?.toLowerCase() === (email || "").toLowerCase()));

export const getAllUserRoles: MemoizedSelector<UserManagementState, UserRole[]> = createSelector(
    selectState,
    (state: UserManagementState) => {
        const values = Object.values(state.userRoles?.result || {});
        return values.length ? values.sort((a, b) => (a.name || "").localeCompare(b.name || "")) : [];
    }
);

export const getUserRolesCallState = createSelector(selectState, (state: UserManagementState) => state?.userRoles.callState);

export const getUserRoles = createSelector(selectState, getAllUserRoles, (state: UserManagementState, userRoles: UserRole[]) =>
    userRoles.filter(role => role.id !== state.customerRoleId)
);

export const getUserRolesObject = createSelector(selectState, (state: UserManagementState) => state.userRoles?.result);

export const getUserRole = (id: number) =>
    createSelector(getUserRolesObject, (userRoles: ClareDbCollection<UserRole>) => userRoles?.[id] || undefined);

const getUserRoleName = (user: User, userRoles: ClareDbCollection<UserRole>): string => {
    let userRole = "Unknown";
    if (user.role) {
        userRole = user.role.name;
    } else if (userRoles?.[user.roleId]) {
        userRole = userRoles[user.roleId].name;
    }
    return userRole;
};

const getCompanyName = (user: User, entities: ClareDbCollection<Entity>) =>
    entities[user.entityId] ? entities[user.entityId].name : user.entityId;

const getUserListData = (user, userRoles, entities?) => ({
    id: user.id,
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    companyName: entities ? getCompanyName(user, entities) : null,
    entityId: user.entityId,
    roleId: user.roleId,
    roleName: getUserRoleName(user, userRoles),
    createdSignin: user.gitkitId ? "Yes" : "",
});

const getMobileUserListData = (user, userRoles) => ({
    id: user.id,
    name: `${user.firstName} ${user.lastName}`,
    email: user.email,
    entityId: user.entityId,
    roleName: getUserRoleName(user, userRoles),
});

const getUserListArray = (users, userRoles, entityId, mobile = false) => {
    const userList = [];
    users.forEach(user => {
        if (entityId === user.entityId) {
            if (!mobile) {
                userList.push(getUserListData(user, userRoles));
            } else {
                userList.push(getMobileUserListData(user, userRoles));
            }
        }
    });

    return userList;
};

export const getEntityStaffList = createSelector(
    getEntityStaffs,
    getUserRolesObject,
    EntitySelectors.selectEntities,
    (users: User[], userRoles: ClareDbCollection<UserRole>, entities: ClareDbCollection<Entity>): Partial<User>[] =>
        users?.map(user => getUserListData(user, userRoles, entities)) || null
);

export const getMobileEntityStaffList = createSelector(
    getEntityStaffs,
    getUserRolesObject,
    EntitySelectors.selectEntities,
    (users: User[], userRoles: ClareDbCollection<UserRole>, entities: ClareDbCollection<Entity>): Partial<User>[] =>
        users.map(user => getMobileUserListData(user, userRoles))
);

export const getAllUserList = createSelector(
    getUsers,
    getUserRolesObject,
    EntitySelectors.selectEntities,
    (users: User[], userRoles: ClareDbCollection<UserRole>, entities: ClareDbCollection<Entity>) =>
        users.map(user => getUserListData(user, userRoles, entities))
);

export const getUserList = (entityId: number) =>
    createSelector(getUsers, getUserRolesObject, (users: User[], userRoles: ClareDbCollection<UserRole>) =>
        getUserListArray(users, userRoles, entityId)
    );
export const getMobileUserList = (entityId: number) =>
    createSelector(getUsers, getUserRolesObject, (users: User[], userRoles: ClareDbCollection<UserRole>) =>
        getUserListArray(users, userRoles, entityId, true)
    );

export const getCallState = createSelector(selectState, (userState: UserManagementState) => userState.users.callState);

export const isUserListLoaded = () => createSelector(getCallState, (userCallState: LoadingState) => userCallState === LoadingState.loaded);
